import React from 'react';
import { OVERLINE } from '@/components/ui/recipes';
import { Input } from '@/components/cms/ui/input';
import { Tile } from '@/components/ui/tile';
import { Chip } from '@/components/ui/chip';
import { cn } from '@/utils/utils';

export function Field({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`mb-4 ${className}`}>{children}</div>;
}

export function FieldHead({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex justify-between items-baseline mb-2">
      <div className={OVERLINE.field}>{label}</div>
      <div className={`font-lureo-font-ui font-lureo-weight-subheading text-lureo-14 tabular-nums leading-[1.3] ${muted ? 'text-lureo-ink-subtle' : 'text-lureo-ink'}`}>{value}</div>
    </div>
  );
}

export function FieldHeadInput({
  label,
  value,
  min,
  max,
  step,
  onChange,
  suffix = '',
  decimals = 2,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  suffix?: string;
  decimals?: number;
}) {
  const [draft, setDraft] = React.useState<string | null>(null);
  const commit = (raw: string) => {
    setDraft(null);
    const n = parseFloat(raw);
    if (Number.isNaN(n)) return;
    onChange(Math.min(max, Math.max(min, Math.round(n / step) * step)));
  };
  return (
    <div className="flex justify-between items-center mb-2">
      <div className={OVERLINE.field}>{label}</div>
      {/* A small box: the value right-aligned in the semibold, its unit inside after it */}
      <Input
        fit
        suffix={suffix || undefined}
        type="text"
        inputMode="decimal"
        aria-label={label}
        value={draft ?? value.toFixed(decimals)}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={(e) => { setDraft(value.toFixed(decimals)); e.currentTarget.select(); }}
        onBlur={(e) => commit(e.currentTarget.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        className="h-auto rounded-lureo-radius border-lureo-line-strong py-0.75 pl-2 pr-1.5 text-lureo-13 leading-[1.3] [&>input]:w-9 [&>input]:text-right [&>input]:[field-sizing:fixed] [&>span]:text-lureo-12 [&>span]:font-lureo-weight-text"
      />
    </div>
  );
}

/**
 * An editor panel's option tile (a layout, an animation, a path): its glyph over its name, the name medium only once chosen.
 * small: a 12px name in the tile's own padding (3D Text's animations).
 */
export function OptionTile({ label, glyph, selected, onClick, small = false }: { label: string; glyph: React.ReactNode; selected: boolean; onClick: () => void; small?: boolean }) {
  return (
    <Tile
      label={<span className={selected ? undefined : 'font-lureo-weight-text'}>{label}</span>}
      glyph={glyph}
      selected={selected}
      onClick={onClick}
      className={small ? 'text-lureo-12' : 'min-h-12 px-1 py-2'}
    />
  );
}

/** A fill choice (Solid, Hollow, Frame): its name alone in the medium weight; with a glyph (an alignment), the glyph over a 12px name in the regular weight. */
export function FillTile({ label, glyph, selected, onClick, disabled }: { label: string; glyph?: React.ReactNode; selected: boolean; onClick: () => void; disabled?: boolean }) {
  if (glyph) {
    return <Tile label={<span className="font-lureo-weight-text">{label}</span>} glyph={glyph} selected={selected} onClick={onClick} disabled={disabled} className="text-lureo-12" />;
  }
  return <Tile label={label} selected={selected} onClick={onClick} disabled={disabled} className="min-h-0 px-2 py-2.5 text-lureo-14" />;
}

/** An editor panel's option pill (an axis, a direction, a subdivision): the chosen one outlined in the brand ink on the card. */
export function OptionPill({ label, selected, onClick, disabled }: { label: string; selected: boolean; onClick: () => void; disabled?: boolean }) {
  return (
    <Chip
      label={label}
      selected={selected}
      onClick={onClick}
      disabled={disabled}
      className={cn('h-auto px-3 py-1.25 text-lureo-13 leading-[1.3]', selected && 'bg-lureo-surface')}
    />
  );
}

export function SubsectionTitle({ children, mt }: { children: React.ReactNode; mt?: boolean }) {
  return (
    <h4 className={`font-lureo-font-ui font-lureo-weight-overline tracking-[0.05em] text-lureo-12 text-lureo-ink-subtle leading-[1.3] mb-2 ${mt ? 'mt-3.5' : ''}`}>
      {children}
    </h4>
  );
}

export function FillGrid({ children, cols = 3 }: { children: React.ReactNode; cols?: 3 | 4 }) {
  const colClass = cols === 4 ? 'grid-cols-4' : 'grid-cols-3';
  return <div className={`grid ${colClass} gap-2`}>{children}</div>;
}

export function SliderTrack({
  Slider,
  value,
  min,
  max,
  step,
  onChange,
  disabled,
}: {
  Slider: React.ComponentType<{ value: number; min: number; max: number; step: number; onChange: (v: number) => void; disabled?: boolean }>;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  disabled?: boolean;
}) {
  return <Slider value={value} min={min} max={max} step={step} onChange={onChange} disabled={disabled} />;
}
